import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Plus, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Pagination } from '@/components/ui/pagination';
import { FirmwareUpdateLogTable } from '@/components/tables/production/firmware-update-log-table';
import { firmwareUpdateLogApi } from '@/api/production/logs';
import { useDebounce } from '@/hooks/use-debounce';
import { useToast } from '@/hooks/use-toast';

export function FirmwareUpdateLogListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState(searchParams.get('serial') || '');
  const { toast } = useToast();

  const page = Number(searchParams.get('page')) || 1;
  const limit = 20;

  // Debounced search to avoid too many API calls
  const debouncedSearch = useDebounce(searchValue, 300);

  const { data: logs, isLoading } = useQuery({
    queryKey: ['firmware-update-logs', { serial: debouncedSearch, page }],
    queryFn: () =>
      firmwareUpdateLogApi.findMany({
        serial: debouncedSearch || undefined,
        page,
        limit,
      }),
  });

  const { data: stats } = useQuery({
    queryKey: ['firmware-update-logs-stats'],
    queryFn: () => firmwareUpdateLogApi.getStats(),
  });

  const exportMutation = useMutation({
    mutationFn: () =>
      firmwareUpdateLogApi.export({
        serial: debouncedSearch || undefined,
      }),
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `firmware-update-logs-${Date.now()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        variant: 'success',
        title: 'Exportação concluída',
        description: 'Os logs foram exportados com sucesso.',
      });
    },
    onError: () => {
      toast({
        title: 'Erro na exportação',
        description: 'Ocorreu um erro ao exportar os logs.',
        variant: 'destructive',
      });
    },
  });

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('serial', value);
    } else {
      newParams.delete('serial');
    }
    newParams.set('page', '1'); // Reset to first page on search
    setSearchParams(newParams);
  };

  const handlePageChange = (newPage: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', String(newPage));
    setSearchParams(newParams);
  };

  const handleExport = () => {
    exportMutation.mutate();
  };

  return (
    <div className="flex-1 space-y-6 p-6">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            Logs de Gravação de Firmware
          </h1>
          <p className="text-sm text-muted-foreground">
            Visualize e gerencie todos os registros de gravação de firmware
          </p>
        </div>

        <Button asChild>
          <Link to="/production/tool/configurator">
            <Plus className="mr-2 h-4 w-4" />
            Nova Gravação
          </Link>
        </Button>
      </div>

      <Separator />

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-1 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Logs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total || 0}</div>
            <p className="text-xs text-muted-foreground">logs registrados</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Bem-sucedidos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.success || 0}</div>
            <p className="text-xs text-muted-foreground">
              configurações com sucesso
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Falhos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.failed || 0}</div>
            <p className="text-xs text-muted-foreground">
              configurações falhas
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Table Section */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Logs de Gravação de Firmware</CardTitle>
          <CardDescription>
            Visualize e gerencie todos os registros de gravação de firmware
          </CardDescription>

          <div className="w-full flex justify-between items-end pt-3">
            <div className="sm:w-96">
              <Input
                value={searchValue}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Serial"
                className="w-60"
              />
            </div>
            <Button
              variant="outline"
              type="button"
              title="Exporte os dados filtrados"
              onClick={handleExport}
              disabled={exportMutation.isPending}
            >
              <Download className="h-5 w-5 mr-2" />
              {exportMutation.isPending ? 'Exportando...' : 'Exportar'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="w-full text-center py-8">
              <p className="text-gray-500">Carregando...</p>
            </div>
          ) : (
            <>
              <FirmwareUpdateLogTable data={logs?.docs || []} />

              {(logs?.pages ?? 1) > 1 && (
                <Pagination
                  currentPage={page}
                  totalPages={logs?.pages ?? 1}
                  totalItems={logs?.total ?? 0}
                  limit={limit}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
